<template>
<section class="pt-8 pt-md-5 pb-8 pb-md-14">
    <div class="container">
        <div class="row">
            <div class="col-12">
                <h1 class="mb-0 fw-bold">Dashboard</h1>
                <p class="mb-6 text-muted">Request statistics for your usage</p>
                <div class="row g-3 mb-4">
                    <div class="col-12 col-md-4">
                        <select class="form-select form-select-xs" name="access_key" v-model="access_key">
                            <option value="">By all tokens</option>
                            <option
                                :value="item.access_key"
                                v-for="item in data.tokens"
                                :key="item.access_key"
                                >{{ item.is_active ? item.name : `${item.name} [deleted]` }}</option>
                        </select>
                    </div>
                    <div class="col-12 col-md-4">
                        <select class="form-select form-select-xs" name="daterange" v-model="daterange">
                            <option disabled>Date range:</option>
                            <option value="">Default</option>
                            <template v-for="item in data.dateranges" :key="item.ident">
                                <option
                                    :value="item.ident"
                                    v-if="displayDaterange(item.ident)"
                                    >{{ item.name }}</option>
                            </template>
                        </select>
                    </div>
                    <div class="col-12 col-md-4">
                        <select class="form-select form-select-xs" name="group_by" v-model="group_by">
                            <option disabled>Group by:</option>
                            <template v-for="item in data.groups" :key="item.ident">
                                <option
                                    :value="item.ident"
                                    v-if="displayGroup(item.ident)"
                                    >{{ item.name }}</option>
                            </template>
                        </select>
                    </div>
                </div>
                <highcharts :options="chartOptions"></highcharts>
                <hr>
                <div style="text-align: right;">
                    Download this report as <a href="#" @click.prevent="downloadCsv">csv</a>
                </div>
                <div class="table-responsive">
                    <vue-good-table
                        :columns="columns"
                        :rows="rows"
                        :pagination-options="paginationOptions"
                        @page-change="onPageChange"
                    >
                        <template #table-row="props">
                            <span>{{ props.formattedRow[props.column.field] }}</span>
                        </template>
                    </vue-good-table>
                </div>
            </div>
        </div>
    </div>
</section>
</template>

<script>
import { Chart } from "highcharts-vue";
import Highcharts from "highcharts";
import { toHour, Filler } from "utils/date";
import { stringToHexColor } from "utils/string";
import tablePagination from "../../mixins/tablePagination"

export default {
    components: {
        highcharts: Chart
    },
    mixins: [tablePagination],
    watch: {
        group_by(val) {
            this.setFilter("group_by", val);
        },
        daterange(val) {
            this.setFilter("daterange", val);
        },
        access_key(val) {
            this.setFilter("access_key", val);
        }
    },
    created() {
        this.$services.Data().get().then(resp => {
            this.data = resp.body;
            this.filter();
        });
    },
    data() {
        let filterData = window.getFilter("dash"),
            group_by = filterData.group_by || "g1_hours",
            daterange = filterData.daterange || "",
            access_key = !filterData.access_key ? "" : filterData.access_key;
        filterData.group_by = group_by;
        filterData.daterange = daterange;
        return {
            columns: [
                { label: "Date", field: "datetime", formatFn: toHour, sortable: false },
                { label: "/v1", field: "v1", sortable: false },
                { label: "/v1/user", field: "v1_user", sortable: false },
                { label: "/v1/media", field: "v1_media", sortable: false },
                { label: "/v1/story", field: "v1_story", sortable: false },
                { label: "/v1/location", field: "v1_location", sortable: false },
                { label: "/v1/hashtag", field: "v1_hashtag", sortable: false },
                { label: "/v1/highlight", field: "v1_highlight", sortable: false },
            ],
            rows: [],
            data: {},
            group_by,
            daterange,
            access_key,
            filterData,
            chartOptions: {
                chart: {
                    type: "line",
                    zoomType: "x",
                    panning: true,
                    panKey: "shift",
                    scrollablePlotArea: {
                        minWidth: 600
                    },
                    backgroundColor: "#2d3238",
                    style: {
                        fontFamily: "'HK Grotesk', sans-serif"
                    }
                },
                title: {
                    text: "", // Statistics
                    style: {
                        color: "#dee2e6"
                    }
                },
                xAxis: {
                    categories: [],
                    labels: {
                        style: {
                            color: "#adb5bd"
                        }
                    },
                    lineColor: "#3a4048",
                    tickColor: "#3a4048"
                },
                yAxis: {
                    title: {
                        text: "Requests",
                        style: {
                            color: "#adb5bd"
                        }
                    },
                    labels: {
                        style: {
                            color: "#adb5bd"
                        }
                    },
                    gridLineColor: "#3a4048"
                },
                legend: {
                    itemStyle: {
                        color: "#dee2e6"
                    },
                    itemHoverStyle: {
                        color: "#00d4aa"
                    }
                },
                plotOptions: {
                    line: {
                        dataLabels: {
                            enabled: true,
                            color: "#dee2e6"
                        },
                        enableMouseTracking: false
                    }
                },
                series: []
            }
        };
    },
    methods: {
        downloadCsv() {
            let items = this.rows;
            if (!items.length) return;
            let keys = Object.keys(items[0]);
            let res = [keys.join(";")];
            items.forEach(item => {
                let row = {...item};
                row["datetime"] = toHour(row["datetime"])
                res.push(keys.map(k => row[k]).join(";"))
            });
            var encodedUri = ["data:text/csv;charset=utf-8", encodeURI(res.join("\n"))];
            window.open(encodedUri.join(","));
        },
        displayDaterange(daterangeId) {
            if (!daterangeId)  // Default
                return true;
            switch (this.group_by) {
            case "g1_hours":
                return parseInt(daterangeId.slice(1)) < 4
            case "g2_days":
                return parseInt(daterangeId.slice(1)) < 5
            case "g4_months":
                return daterangeId == "f6"
            }
            return true;
        },
        displayGroup(groupId) {
            if (!this.daterange) // Default
                return true;
            let daterangeId = parseInt(this.daterange.slice(1));
            switch (groupId) {
            case "g1_hours":
                return daterangeId < 4;
            case "g2_days":
                return daterangeId < 5;
            }
            return true;
        },
        setFilter(key, val) {
            this.filterData[key] = val;
            window.setFilter("dash", this.filterData);
            this.filter();
        },
        filter(data) {
            let params = Object.assign(data || {}, this.filterData)
            if (!this.displayDaterange(params.daterange) || !this.displayGroup(params.group_by)) {
                console.warn("Wrong filter", params)
                return
            }
            this.$services.Stats().get(params).then(resp => {
                const counters = resp.body.counters
                const filler = new Filler(params.group_by, counters, resp.body.days)
                // Chart
                let categories = []
                let series = {}
                // Build chart series from tokens
                let tokens = this.data.tokens
                if (this.access_key) {
                    // Display only all-in-one and current Token
                    tokens = tokens.filter(v => this.access_key == v.access_key)
                } else {
                    series[""] = {
                        "name": "All my tokens",
                        "data": [],
                        "dashStyle": "longdash",
                        "color": "#c4c4c4",
                        "lineWidth": 2,
                        "filler": filler
                    }
                }
                tokens.forEach((v, k) => {
                    series[v.access_key] = {
                        "name": v.is_active ? v.name : `${v.name} [deleted]`,
                        "data": [],
                        "color": stringToHexColor(v.name),
                        "dashStyle": "solid",
                        "filler": new Filler(params.group_by, counters, resp.body.days)
                    }
                })
                // Chart and table
                let allTokenItems = {}
                // let items = [...resp.body.data, filler.tombstone]
                for (let item of resp.body.data) {
                    let ser = series[item.access_key];
                    let rows = ser.filler.run(item);
                    for (let r of rows) {
                        ser.data.push(r.v1);
                        if (r.datetime in allTokenItems) {
                            counters.forEach(k => allTokenItems[r.datetime][k] += r[k]);
                        } else {
                            allTokenItems[r.datetime] = r;
                            categories.push(toHour(r.datetime));
                        }
                    }
                }
                setTimeout(() => {
                    let items = Object.values(allTokenItems);
                    if (series[""])
                        series[""].data = items.map(r => r.v1)
                    items.reverse();
                    this.chartOptions.xAxis.categories = categories;
                    this.chartOptions.series = Object.values(series);
                    this.rows = items;
                })
            })
        },
        onPageChange(params) {
            this.filter({ page: params.currentPage });
        }
    }
};
</script>
<style>
    .table th {
    font-size: 12px;
    white-space: nowrap;
    }
    .table td {
    font-size: 12px;
    }
</style>
